import GridBox from '@components/atoms/GridBox';
import Section from '@components/atoms/Section';
import Spacing from '@components/atoms/Spacing';
import {
  Heading1,
  Heading2,
  Heading3,
  LightParagraph,
  Paragraph,
} from '@components/atoms/Typography';
import PageContainer from '@components/molecules/PageContainer';
import RichTextSection from '@components/molecules/RichTextSection';
import { SliceButtonLink } from '@components/molecules/SliceZone';
import { Note } from '@components/molecules/slices/SliceNote';
import AutoLoanRatesList from '@components/organisms/AutoLoanRatesList';
import CarriersLogoList from '@components/organisms/CarriersLogoList';
import LoanResumeCard from '@components/organisms/card/LoanResumeCard';
import SimpleNavBar from '@components/organisms/navbar/SimpleNavBar';
import BREAKPOINTS from '@lib/constants/breakpoints';
import COLORS from '@lib/constants/colors';
import ROUTES from '@lib/constants/routes';
import { FipeVehicleType } from '@prisma/client';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

const Value = styled.span`
  display: flex;
  justify-content: space-between;
  font-size: 42px;
  line-height: 52px;
  font-weight: 500;
  width: 100%;
  align-items: center;

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    font-size: 28px;
    line-height: 30px;
  }
`;

const Title = styled(Heading3)`
  margin-bottom: 16px !important;
  font-size: 24px !important;
  line-height: 1.25;
  display: block;
  font-weight: normal;

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    font-size: 20px !important;
    margin-bottom: 4px !important;
  }
`;

const Input = styled.input`
  font-size: 42px;
  line-height: 52px;
  font-weight: 500;
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  color: ${COLORS.black};

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    font-size: 28px;
    line-height: 30px;
  }
`;

const ControlButtons = styled.div`
  display: grid;
  align-items: center;
  justify-content: center;
  grid-template-columns: 1fr 1fr;
  grid-gap: 8px;
`;

const Button = styled.button`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  font-weight: 500;
  border: none;
  background-color: ${COLORS.pink};
  border-radius: 24px;
  height: 42px;
  font-size: 16px;
  line-height: 1.5;
  transition: 0.2s ease;
  cursor: pointer;
  width: 42px;
  padding: 0;
  margin: 0 !important;
`;

const Range = styled.input`
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 2px;
  border-radius: 5px;
  background: ${COLORS.gray};
  outline: none;
  -webkit-transition: 0.2s;
  transition: opacity 0.2s;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: ${COLORS.pink};
    cursor: pointer;
  }
`;

const Container = styled(PageContainer)`
  background-color: ${COLORS.pastel};

  nav:first-child {
    background-color: ${COLORS.pastel};
    border-color: transparent;
  }

  section {
    /* max-width: none; */
  }

  @media screen and (min-width: ${BREAKPOINTS.medium}) {
    h1 {
      font-size: 42px;
    }
  }

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    h1 {
      font-size: 28px;
    }
  }
`;

const Header = styled.header`
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin-top: 64px;

  grid-gap: 32px;

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    grid-template-columns: 1fr;
    grid-gap: 32px;
    margin: 0;
  }
`;

export default function Page() {
  const router = useRouter();

  const modelYear = Number(router.query.year);

  const [entrada, setEntrada] = useState(0);
  const [parcelas, setParcelas] = useState(36);
  const [totalValue, setTotalValue] = useState(10000);
  const steps = 12;

  useEffect(() => {
    const total = router.query.value ? Number(router.query.value) : 5000000;

    setEntrada(total * 0.3);
    setTotalValue(total);
  }, [router.query.value]);

  useEffect(() => {
    if (totalValue <= 0) setTotalValue(1000);
    if (totalValue > 10000000000) setTotalValue(10000000000);
  }, [totalValue, setTotalValue]);

  useEffect(() => {
    if (entrada < 0) setEntrada(0);
    if (entrada > totalValue) setEntrada(totalValue);
  }, [entrada, totalValue, setEntrada]);

  if (!totalValue) return <></>;

  return (
    <Container
      title="Confira o valor do Financiamento do seu veículo"
      metaDescription="Confira o valor do Financiamento do seu veículo e simule as condições ideais para você."
      showNavBar={false}
      // showFooter={false}
    >
      <SimpleNavBar hasBorder />
      <Section pastel>
        <Header>
          <div>
            <Heading1>
              Chegou a hora de simular as condições ideais para você
            </Heading1>
            <LightParagraph>
              Lembre-se: as condições escolhidas aqui não são finais e são
              apenas uma simulação com os dados do Banco Central.
            </LightParagraph>
            <Spacing bottom={2} />
            <Title>Qual o valor do veículo?</Title>
            <GridBox flexDirection="row" flexWrap="nowrap" alignItems="center">
              <Value>
                <span>R$</span>
                <Input
                  value={(totalValue / 100).toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                  onChange={(e) => {
                    setTotalValue(Number(e.target.value.replace(/\D/g, '')));
                  }}
                />
              </Value>
              <ControlButtons>
                <Button
                  onClick={() => {
                    setTotalValue(totalValue - 100000);
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                  >
                    <rect
                      width="14"
                      height="2"
                      x="5"
                      y="11"
                      fill="#ffffff"
                      fillRule="evenodd"
                      rx="1"
                    />
                  </svg>
                </Button>
                <Button
                  onClick={() => {
                    setTotalValue(totalValue + 100000);
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill="#ffffff"
                      fillRule="evenodd"
                      d="M13 11h5a1 1 0 0 1 0 2h-5v5a1 1 0 0 1-2 0v-5H6a1 1 0 0 1 0-2h5V6a1 1 0 0 1 2 0v5z"
                    />
                  </svg>
                </Button>
              </ControlButtons>
            </GridBox>
            <Spacing bottom={2} />
            <Title>E de entrada, qual valor você gostaria de dar?</Title>
            <GridBox flexDirection="row" flexWrap="nowrap" alignItems="center">
              <Value>
                <span>R$</span>
                <Input
                  value={(entrada / 100).toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setEntrada(Number(value));
                  }}
                />
              </Value>
              <ControlButtons>
                <Button
                  onClick={() => {
                    setEntrada(entrada - 100000);
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                  >
                    <rect
                      width="14"
                      height="2"
                      x="5"
                      y="11"
                      fill="#ffffff"
                      fillRule="evenodd"
                      rx="1"
                    />
                  </svg>
                </Button>
                <Button
                  onClick={() => {
                    setEntrada(entrada + 100000);
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill="#ffffff"
                      fillRule="evenodd"
                      d="M13 11h5a1 1 0 0 1 0 2h-5v5a1 1 0 0 1-2 0v-5H6a1 1 0 0 1 0-2h5V6a1 1 0 0 1 2 0v5z"
                    />
                  </svg>
                </Button>
              </ControlButtons>
            </GridBox>
            <Spacing bottom={2} />
            <Title>Em quantos meses você gostaria de pagar o restante?</Title>
            <Value>{parcelas} Meses</Value>
            <Range
              type="range"
              min="1"
              max="5"
              value={parcelas / steps}
              step="1"
              onChange={(e) => setParcelas(steps * Number(e.target.value))}
            />
          </div>
          <div>
            <LoanResumeCard
              totalValue={totalValue}
              entrada={entrada}
              parcelas={parcelas}
              modelYear={modelYear}
              type={router.query.type as FipeVehicleType}
            />
          </div>
        </Header>
      </Section>
      <Section style={{ padding: 0 }}>
        <RichTextSection>
          <h2>Simulador de financiamento automotivo</h2>
          <p>
            Está se preparando para comprar o seu próximo veículo, mas está
            incerto quanto ao valor do pagamento ou custo mensal? Calcular esse
            valor geralmente envolve um pouco de matemática, mas a boa notícia é
            que o calculador de financiamento automotivo da Luna faz todo o
            trabalho pesado por você.
          </p>

          <Heading2 id="bancos">
            Valor do Financiamento em cada Instituição
          </Heading2>

          <AutoLoanRatesList
            entrada={entrada}
            meses={parcelas}
            total={totalValue}
          />

          <h2>Como funciona a calculadora de financiamento automotivo?</h2>
          <p>
            Digamos que você esteja de olho em um carro compacto ou um SUV.
            Escolha a marca e o modelo que deseja ou, alternativamente, insira o
            preço do veículo na calculadora de financiamento. O sistema
            solicitará outros detalhes como entrada, prazo do empréstimo, valor
            de troca e taxa de juros - nenhum dado pessoal, ok?! Após isso, ele
            calculará os juros compostos, estimará as taxas de imposto e título,
            e exibirá o pagamento mensal e anual.
          </p>
          <Note>
            Dica de Especialista: Esta calculadora ajuda você a visualizar como
            mudanças na taxa de juros, entrada, valor de troca e preço do
            veículo afetam o seu empréstimo. Reserve um tempo para experimentar
            diferentes valores e encontrar a configuração de empréstimo para
            automóvel que melhor se adapta ao seu orçamento.
          </Note>

          <h2>Considere todos os aspectos do financiamento</h2>
          <p>
            O pagamento mensal é o melhor indicador de como o empréstimo do
            veículo impactará seu orçamento. Ele oferece um{' '}
            {`"choque de
            realidade"`}{' '}
            sobre se você pode ou não arcar com o veículo. Embora essa seja a
            figura mais fácil de entender, não é o único número de que você deve
            estar ciente.
          </p>
          <h3>Regras Gerais</h3>
          <ul>
            <li>
              O pagamento do empréstimo não deve ser superior a 15% do seu
              salário líquido.
            </li>
            <li>
              O prazo do empréstimo deve ser idealmente inferior a 60 meses.
            </li>
            <li>Você deve mirar em uma entrada de pelo menos 10%.</li>
          </ul>
          <Note>
            Cada situação é única, então essas recomendações não são imutáveis.
            Além disso, esses números diferem para quem opta por outros serviços
            financeiros. Consulte nossos artigos para informações específicas a
            esse cenário.
          </Note>
          <h2>Financiamento por tipo de veículo</h2>

          <p>
            O valor do financiamento varia de acordo com o tipo de veículo que
            você deseja financiar. Aqui na Luna montamos um guia para você
            entender melhor como funciona o financiamento de cada tipo de
            veículo.
          </p>
          <Heading2>Proteja seu veículo com nossos seguros</Heading2>
          <CarriersLogoList />
          <Paragraph>
            A Luna oferece os melhores seguros para você proteger seu veículo.
            São diversas opções de coberturas para você escolher a que melhor se
            encaixa no seu perfil.
          </Paragraph>
          <SliceButtonLink
            slice={{
              primary: {
                text: `Simular Seguro`,
                href: ROUTES.startForm(),
              },
            }}
          />
          <ul>
            <li>
              <Link
                passHref
                href={ROUTES.financiamento.type({ type: FipeVehicleType.car })}
              >
                <a>Guia de Financiamento de Carros</a>
              </Link>
            </li>
            <li>
              <Link
                passHref
                href={ROUTES.financiamento.type({
                  type: FipeVehicleType.motorcycle,
                })}
              >
                <a>Guia de Financiamento de Motos</a>
              </Link>
            </li>
            <li>
              <Link
                passHref
                href={ROUTES.financiamento.type({
                  type: FipeVehicleType.trucks,
                })}
              >
                <a>Guia de Financiamento de Caminhões</a>
              </Link>
            </li>
          </ul>
        </RichTextSection>
      </Section>
    </Container>
  );
}
